import React, { useState, useEffect, useCallback } from 'react';
import { 
  Ticket, Clock, CheckCircle, AlertCircle, AlertTriangle,
  MessageSquare, Send, RefreshCw, Filter, Search, User, Calendar,
  Bug, Mic, Monitor, Lightbulb, HelpCircle, Loader2, ExternalLink,
  ChevronDown, BarChart3
} from 'lucide-react';
import { MainLayout } from '../components/layout/MainLayout';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500', textColor: 'text-blue-400', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'bg-amber-500', textColor: 'text-amber-400', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-emerald-500', textColor: 'text-emerald-400', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-500', textColor: 'text-gray-400', icon: CheckCircle },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-gray-500', textColor: 'text-gray-400' },
  medium: { label: 'Medium', color: 'bg-amber-500', textColor: 'text-amber-400' },
  high: { label: 'High', color: 'bg-orange-500', textColor: 'text-orange-400' },
  critical: { label: 'Critical', color: 'bg-red-500', textColor: 'text-red-400' },
};

const CATEGORY_ICONS = {
  bug: Bug,
  voice: Mic,
  ui: Monitor,
  feature: Lightbulb,
  other: HelpCircle,
  general: HelpCircle,
  network: AlertTriangle,
};

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [newResponse, setNewResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPriority !== 'all') params.append('priority', filterPriority);
      
      const response = await axios.get(
        `${API_URL}/api/tickets/admin/all?${params.toString()}`,
        { withCredentials: true }
      );
      setTickets(response.data.tickets || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/tickets/admin/${ticketId}`,
        { status },
        { withCredentials: true }
      );
      toast.success(`Ticket status updated to ${status}`);
      loadTickets();
      if (selectedTicket?.ticket_id === ticketId) {
        const response = await axios.get(
          `${API_URL}/api/tickets/admin/${ticketId}`,
          { withCredentials: true }
        );
        setSelectedTicket(response.data);
      }
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const updateTicketPriority = async (ticketId, priority) => {
    try {
      await axios.put(
        `${API_URL}/api/tickets/admin/${ticketId}`,
        { priority },
        { withCredentials: true }
      );
      toast.success(`Priority updated to ${priority}`);
      loadTickets();
      if (selectedTicket?.ticket_id === ticketId) {
        const response = await axios.get(
          `${API_URL}/api/tickets/admin/${ticketId}`,
          { withCredentials: true }
        );
        setSelectedTicket(response.data);
      }
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const handleSendResponse = async () => {
    if (!newResponse.trim() || !selectedTicket) return;
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/tickets/admin/${selectedTicket.ticket_id}/respond`,
        { message: 'Support Response', content: newResponse },
        { withCredentials: true }
      );
      toast.success('Response sent to user');
      setNewResponse('');
      const response = await axios.get(
        `${API_URL}/api/tickets/admin/${selectedTicket.ticket_id}`,
        { withCredentials: true }
      );
      setSelectedTicket(response.data);
      loadTickets();
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedTicket) return;
    
    try {
      await axios.put(
        `${API_URL}/api/tickets/admin/${selectedTicket.ticket_id}`,
        { 
          status: 'resolved',
          resolution_notes: resolutionNotes || 'Issue has been resolved.'
        },
        { withCredentials: true }
      );
      toast.success('Ticket resolved');
      setResolutionNotes('');
      loadTickets();
      const response = await axios.get(
        `${API_URL}/api/tickets/admin/${selectedTicket.ticket_id}`,
        { withCredentials: true }
      );
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Failed to resolve ticket');
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = !searchQuery || 
      ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.ticket_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.user_email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${window.location.origin}${url}`;
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="admin-tickets-title">
                <Ticket className="h-6 w-6 text-orange-400" />
                Support Tickets Management
              </h1>
              <p className="text-muted-foreground text-sm">Manage and respond to user reported issues</p>
            </div>
            <button
              onClick={loadTickets}
              className="p-2 hover:bg-accent rounded-lg transition-colors"
              title="Refresh"
              data-testid="refresh-admin-tickets-btn"
            >
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-card rounded-xl p-4 border border-border" data-testid="stat-total">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <BarChart3 className="h-4 w-4" />
                <span className="text-sm">Total</span>
              </div>
              <p className="text-2xl font-bold">{stats.total || 0}</p>
            </div>
            <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30" data-testid="stat-open">
              <div className="flex items-center gap-2 text-blue-400 mb-1">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">Open</span>
              </div>
              <p className="text-2xl font-bold text-blue-400">{stats.open || 0}</p>
            </div>
            <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30" data-testid="stat-in-progress">
              <div className="flex items-center gap-2 text-amber-400 mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-sm">In Progress</span>
              </div>
              <p className="text-2xl font-bold text-amber-400">{stats.in_progress || 0}</p>
            </div>
            <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/30" data-testid="stat-resolved">
              <div className="flex items-center gap-2 text-emerald-400 mb-1">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">Resolved</span>
              </div>
              <p className="text-2xl font-bold text-emerald-400">{stats.resolved || 0}</p>
            </div>
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30" data-testid="stat-critical">
              <div className="flex items-center gap-2 text-red-400 mb-1">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm">Critical</span>
              </div>
              <p className="text-2xl font-bold text-red-400">{stats.critical_open || 0}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search tickets, users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-xl placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="search-admin-tickets-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-card border border-border rounded-xl px-3 py-2 focus:outline-none"
                data-testid="filter-status-select"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="bg-card border border-border rounded-xl px-3 py-2 focus:outline-none"
                data-testid="filter-priority-select"
              >
                <option value="all">All Priority</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Ticket List */}
            <div className="lg:col-span-1 space-y-3 max-h-[70vh] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="text-center py-12 bg-card rounded-xl border border-border">
                  <Ticket className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No tickets found</p>
                </div>
              ) : (
                filteredTickets.map((ticket) => {
                  const CategoryIcon = CATEGORY_ICONS[ticket.category] || HelpCircle;
                  const isSelected = selectedTicket?.ticket_id === ticket.ticket_id;
                  
                  return (
                    <button
                      key={ticket.ticket_id}
                      onClick={() => setSelectedTicket(ticket)}
                      data-testid={`admin-ticket-item-${ticket.ticket_id}`}
                      className={`w-full text-left p-4 rounded-xl border transition-all ${
                        isSelected 
                          ? 'bg-blue-500/20 border-blue-500/50' 
                          : 'bg-card border-border hover:bg-accent'
                      } ${ticket.priority === 'critical' ? 'border-l-4 border-l-red-500' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <CategoryIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs font-mono text-muted-foreground">#{ticket.ticket_id}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CONFIG[ticket.status]?.color} text-white`}>
                          {STATUS_CONFIG[ticket.status]?.label}
                        </span>
                      </div>
                      <h3 className="font-medium truncate">{ticket.title}</h3>
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <User className="h-3 w-3" />
                        <span className="truncate">{ticket.user_email}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-muted-foreground">{formatDate(ticket.created_at)}</span>
                        <span className={`text-xs ${PRIORITY_CONFIG[ticket.priority]?.textColor}`}>
                          {ticket.priority}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Ticket Detail */}
            <div className="lg:col-span-2">
              {selectedTicket ? (
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  {/* Ticket Header */}
                  <div className="p-4 border-b border-border">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-mono text-muted-foreground">#{selectedTicket.ticket_id}</span>
                        </div>
                        <h2 className="text-xl font-semibold">{selectedTicket.title}</h2>
                      </div>
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="flex flex-wrap gap-2">
                      <div className="relative group">
                        <button 
                          className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${STATUS_CONFIG[selectedTicket.status]?.color}`}
                          data-testid="status-dropdown-btn"
                        >
                          {selectedTicket.status.replace('_', ' ')}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        <div className="absolute top-full left-0 mt-1 bg-popover rounded-lg border border-border shadow-xl hidden group-hover:block z-10">
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                            <button
                              key={key}
                              onClick={() => updateTicketStatus(selectedTicket.ticket_id, key)}
                              className="block w-full text-left px-4 py-2 text-sm hover:bg-accent first:rounded-t-lg last:rounded-b-lg"
                              data-testid={`status-option-${key}`}
                            >
                              {config.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="relative group">
                        <button 
                          className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 bg-accent ${PRIORITY_CONFIG[selectedTicket.priority]?.textColor}`}
                          data-testid="priority-dropdown-btn"
                        >
                          {selectedTicket.priority}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        <div className="absolute top-full left-0 mt-1 bg-popover rounded-lg border border-border shadow-xl hidden group-hover:block z-10">
                          {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                            <button
                              key={key}
                              onClick={() => updateTicketPriority(selectedTicket.ticket_id, key)}
                              className={`block w-full text-left px-4 py-2 text-sm hover:bg-accent first:rounded-t-lg last:rounded-b-lg ${config.textColor}`}
                              data-testid={`priority-option-${key}`}
                            >
                              {config.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* User Info */}
                    <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {selectedTicket.user_name} ({selectedTicket.user_email})
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(selectedTicket.created_at)}
                      </span>
                      {selectedTicket.page_url && (
                        <a 
                          href={getPageUrl(selectedTicket.page_url)}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-400 hover:underline"
                          data-testid="view-page-link"
                        >
                          <ExternalLink className="h-4 w-4" />
                          View Page
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="p-4 border-b border-border">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Description</h3>
                    <p className="whitespace-pre-wrap">{selectedTicket.description}</p>
                    
                    {selectedTicket.error_message && (
                      <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-xs font-medium text-red-400 mb-1">Error Message:</p>
                        <p className="text-sm font-mono">{selectedTicket.error_message}</p>
                        {selectedTicket.error_stack && (
                          <details className="mt-2">
                            <summary className="text-xs text-muted-foreground cursor-pointer">Stack trace</summary>
                            <pre className="mt-1 text-xs text-muted-foreground overflow-x-auto">{selectedTicket.error_stack}</pre>
                          </details>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Conversation */}
                  <div className="p-4 border-b border-border max-h-48 overflow-y-auto">
                    <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Conversation
                    </h3>
                    
                    {selectedTicket.responses?.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No responses yet</p>
                    ) : (
                      <div className="space-y-3">
                        {selectedTicket.responses?.map((response) => (
                          <div 
                            key={response.id}
                            className={`p-3 rounded-lg ${
                              response.from === 'admin' 
                                ? 'bg-blue-500/10 border border-blue-500/30 ml-4' 
                                : 'bg-accent border border-border mr-4'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-medium ${
                                response.from === 'admin' ? 'text-blue-400' : 'text-muted-foreground'
                              }`}>
                                {response.from === 'admin' ? 'You (Support)' : response.user_name || 'User'}
                              </span>
                              <span className="text-xs text-muted-foreground">{formatDate(response.created_at)}</span>
                            </div>
                            <p className="text-sm">{response.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Reply Box */}
                  <div className="p-4 space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newResponse}
                        onChange={(e) => setNewResponse(e.target.value)}
                        placeholder="Type a response to the user..."
                        className="flex-1 px-4 py-2 bg-card border border-border rounded-xl placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onKeyDown={(e) => e.key === 'Enter' && handleSendResponse()}
                        disabled={submitting}
                        data-testid="admin-response-input"
                      />
                      <button
                        onClick={handleSendResponse}
                        disabled={!newResponse.trim() || submitting}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                        data-testid="send-admin-response-btn"
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send size={16} />}
                      </button>
                    </div>
                    
                    {/* Resolve Section */}
                    {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                      <div className="pt-3 border-t border-border">
                        <input
                          type="text"
                          value={resolutionNotes}
                          onChange={(e) => setResolutionNotes(e.target.value)}
                          placeholder="Resolution notes (optional)..."
                          className="w-full px-4 py-2 bg-card border border-border rounded-xl placeholder-muted-foreground focus:outline-none mb-2"
                          data-testid="resolution-notes-input"
                        />
                        <button
                          onClick={handleResolve}
                          className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                          data-testid="resolve-ticket-btn"
                        >
                          <CheckCircle size={16} />
                          Mark as Resolved
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-card rounded-xl border border-border p-12 text-center">
                  <Ticket className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground">Select a ticket</h3>
                  <p className="text-sm text-muted-foreground">Click on a ticket to view and respond</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminTickets;
